import React from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { cn } from "@/lib/utils";
import { remarkUnderline } from "@/lib/remarkUnderline";
import { parseInternalReferenceHref, teamReferenceRoute } from "@/lib/contentReferences";
import MemberProfileHover from "@/components/MemberProfileHover";

const schema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), "u"],
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a || []), "href", "title", "target", "rel"],
    code: [...(defaultSchema.attributes?.code || []), "className"],
  },
};

/** Split unicode emoji (incl. ZWJ / FE0F sequences) for slightly larger display. */
let EMOJI_SPLIT = null;
try {
  // Runtime construction — webpack's parser rejects unicode property escapes in literals.
  EMOJI_SPLIT = new RegExp(
    "(\\p{Extended_Pictographic}(?:\\uFE0F|\\u200D\\p{Extended_Pictographic}|\\p{Emoji_Modifier})*)",
    "gu"
  );
} catch {
  EMOJI_SPLIT = null;
}

function enlargeEmojisInChildren(children) {
  if (!EMOJI_SPLIT) return children;
  return React.Children.map(children, (child) => {
    if (typeof child !== "string") return child;
    EMOJI_SPLIT.lastIndex = 0;
    if (!EMOJI_SPLIT.test(child)) return child;
    EMOJI_SPLIT.lastIndex = 0;
    const parts = child.split(EMOJI_SPLIT);
    return parts.map((part, i) => {
      if (!part) return null;
      EMOJI_SPLIT.lastIndex = 0;
      if (EMOJI_SPLIT.test(part)) {
        return (
          <span
            key={`e-${i}`}
            className="inline-block text-[1.35em] leading-none align-[-0.1em]"
          >
            {part}
          </span>
        );
      }
      return <React.Fragment key={`t-${i}`}>{part}</React.Fragment>;
    });
  });
}

function withEnlargedEmoji(Tag) {
  return ({ node, children, ...props }) => (
    <Tag {...props}>{enlargeEmojisInChildren(children)}</Tag>
  );
}

function ReferenceAnchor({
  href,
  children,
  membersById,
  inverse,
  className,
  ...props
}) {
  const parsed = parseInternalReferenceHref(href);
  if (!parsed) {
    return (
      <a
        {...props}
        href={href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  if (parsed.type === "channel") {
    return (
      <Link
        to={href}
        className={cn(
          "font-medium no-underline hover:underline",
          inverse
            ? "text-primary-foreground decoration-primary-foreground/70"
            : "text-primary",
          className
        )}
      >
        {children}
      </Link>
    );
  }

  if (parsed.type === "team") {
    const to =
      teamReferenceRoute(parsed.orgId, parsed.targetId) || href;
    return (
      <Link
        to={to}
        className={cn(
          "font-medium no-underline hover:underline",
          inverse
            ? "text-primary-foreground decoration-primary-foreground/70"
            : "text-primary",
          className
        )}
      >
        {children}
      </Link>
    );
  }

  const member = membersById?.[parsed.targetId];
  const labelText =
    typeof children === "string"
      ? children
      : React.Children.toArray(children).join("");
  const displayName = String(labelText || "").replace(/^@/, "") || "Member";
  const user = member?.user
    ? { ...member.user, title: member.title || member.user.title }
    : member || {
        user_id: parsed.targetId,
        first_name: displayName,
        last_name: "",
      };

  return (
    <MemberProfileHover user={user} side="top" align="start">
      <span
        className={cn(
          "font-medium cursor-pointer hover:underline",
          inverse ? "text-primary-foreground" : "text-primary",
          className
        )}
        role="button"
        tabIndex={0}
      >
        {children}
      </span>
    </MemberProfileHover>
  );
}

/**
 * Sanitized markdown renderer for post bodies.
 * @param {boolean} [inverse] - Links/code for use on primary (e.g. own DM bubbles)
 * @param {Record<string, object>} [membersById] - Optional org member lookup for @mentions
 */
const MarkdownBody = ({
  content,
  className,
  compact = false,
  inverse = false,
  membersById,
}) => {
  const text = content == null ? "" : String(content);

  if (!text.trim()) {
    return null;
  }

  return (
    <div
      className={cn(
        "markdown-body break-words",
        inverse ? "text-primary-foreground" : "text-foreground",
        compact
          ? "text-sm [&_p]:mb-1 [&_p:last-child]:mb-0"
          : "text-sm sm:text-base [&_p]:mb-3 [&_p:last-child]:mb-0",
        "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
        "[&_li]:my-0.5",
        "[&_u]:underline",
        "[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mb-2",
        "[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mb-2",
        "[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mb-1",
        inverse
          ? "[&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-primary-foreground/15 [&_pre]:p-3 [&_pre]:text-xs [&_code]:rounded [&_code]:bg-primary-foreground/15 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs"
          : "[&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_pre]:text-xs [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs",
        "[&_pre_code]:bg-transparent [&_pre_code]:p-0",
        inverse
          ? "[&_a]:text-primary-foreground [&_a]:underline [&_a]:decoration-primary-foreground/70 [&_a]:underline-offset-2"
          : "[&_a]:text-primary [&_a]:underline",
        inverse
          ? "[&_blockquote]:border-l-2 [&_blockquote]:border-primary-foreground/40 [&_blockquote]:pl-3 [&_blockquote]:opacity-90"
          : "[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkUnderline]}
        rehypePlugins={[[rehypeSanitize, schema]]}
        components={{
          a: ({ node, href, children, className: linkClass, ...props }) => (
            <ReferenceAnchor
              href={href}
              membersById={membersById}
              inverse={inverse}
              className={linkClass}
              {...props}
            >
              {enlargeEmojisInChildren(children)}
            </ReferenceAnchor>
          ),
          u: withEnlargedEmoji("u"),
          p: withEnlargedEmoji("p"),
          li: withEnlargedEmoji("li"),
          strong: withEnlargedEmoji("strong"),
          em: withEnlargedEmoji("em"),
          h1: withEnlargedEmoji("h1"),
          h2: withEnlargedEmoji("h2"),
          h3: withEnlargedEmoji("h3"),
          td: withEnlargedEmoji("td"),
          th: withEnlargedEmoji("th"),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownBody;
